import React, { createContext, useState, useContext, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { createTheme, ThemeProvider as MUIThemeProvider } from "@material-ui/core/styles";
import { ptBR } from "@material-ui/core/locale";
import { CssBaseline } from "@material-ui/core";

const ThemeContext = createContext();

const buildPalette = (darkMode) =>
  darkMode
    ? {
        type: "dark",
        primary: { main: "#4F8FFF", light: "#7BAEFF", dark: "#2E6BE0" },
        secondary: { main: "#F5A524", light: "#FFC46B", dark: "#C97F0B" },
        background: { default: "#0F172A", paper: "#1E293B" },
      }
    : {
        type: "light",
        primary: { main: "#2563EB", light: "#5B8DF6", dark: "#1B45B0" },
        secondary: { main: "#F59E0B", light: "#FFC24D", dark: "#B97400" },
        background: { default: "#F4F6FA", paper: "#FFFFFF" },
      };

export const ThemeProvider = ({ children }) => {
  const [darkMode, setDarkMode] = useState(
    () => localStorage.getItem("wppcontabil.darkMode") === "true"
  );

  useEffect(() => {
    localStorage.setItem("wppcontabil.darkMode", String(darkMode));
  }, [darkMode]);

  const toggleTheme = () => {
    setDarkMode((prevMode) => !prevMode);
  };

  const theme = useMemo(
    () =>
      createTheme(
        {
          palette: buildPalette(darkMode),
          shape: { borderRadius: 8 },
          scrollbarStyles: {
            "&::-webkit-scrollbar": {
              width: "8px",
              height: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              boxShadow: "inset 0 0 6px rgba(0, 0, 0, 0.3)",
              backgroundColor: darkMode ? "#334155" : "#e8e8e8",
            },
          },
          overrides: {
            MuiPaper: {
              rounded: { borderRadius: 8 },
            },
            MuiButton: {
              root: { textTransform: "none", fontWeight: 600 },
            },
          },
        },
        ptBR
      ),
    [darkMode]
  );

  const contextValue = useMemo(() => ({ darkMode, toggleTheme }), [darkMode]);

  return (
    <ThemeContext.Provider value={contextValue}>
      <MUIThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MUIThemeProvider>
    </ThemeContext.Provider>
  );
};
ThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useThemeContext = () => useContext(ThemeContext);
